'use client';

import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';

import type { Placement } from '@repo/lib/position';
import {
  Button,
  type ButtonMode,
  type DefaultIconSize,
  type IconShapeVariant,
} from '@repo/ui/Button';
import { FloatingContent } from '@repo/ui/FloatingContent';
import { Icon } from '@repo/ui/Icon';
import { type IconNames } from '@repo/ui/Icon/utils';
import { VisuallyHidden } from '@repo/ui/VisuallyHidden';

import { Tooltip } from '@repo/ui/Tooltip';
import { TooltipRoot } from '@repo/ui/Tooltip/TooltipRoot';
import { TooltipTrigger } from '@repo/ui/Tooltip/TooltipTrigger';

export interface IconButtonProps extends ComponentPropsWithRef<'button'> {
  ariaDisabled?: boolean;
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  icon: IconNames;
  isLoading?: boolean;
  label: string;
  loadingLabel?: string;
  mode?: ButtonMode;
  size?: DefaultIconSize;
  tooltipPlacement?: Placement;
  variant?: IconShapeVariant;
}

export const IconButton = (props: IconButtonProps) => {
  const {
    asChild = false,
    children,
    disabled = false,
    icon,
    isLoading = false,
    label,
    size = 'md',
    tooltipPlacement = 'top',
    variant = 'primary',
    ...attrs
  } = props;

  const iconContent = (
    <>
      <Icon name={icon} size="sm" />
      <VisuallyHidden>{label}</VisuallyHidden>
    </>
  );

  let buttonChildren: ReactNode = iconContent;

  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement<{ children?: ReactNode }>(child)) {
      throw new Error(
        'IconButton: child must be a valid React element when asChild is true',
      );
    }
    buttonChildren = cloneElement(child, undefined, iconContent);
  }

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <Button
          asChild={asChild}
          disabled={disabled}
          isLoading={isLoading}
          shape="icon"
          size={size}
          variant={variant}
          {...attrs}
        >
          {buttonChildren}
        </Button>
      </TooltipTrigger>
      <Tooltip placement={tooltipPlacement}>
        <FloatingContent variant="tooltip">{label}</FloatingContent>
      </Tooltip>
    </TooltipRoot>
  );
};
